/* cSpell:disable */

import { useCallback, useMemo, useRef } from 'react';
import { Platform } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { openBrowserAsync } from 'expo-web-browser';
import { logger as LOG, useBreakpointHelpers } from '@sweetgreen/garnish';

import { useIsCustomerSessionReady } from '@order/hooks';
import { useTelemetry } from '@order/Telemetry';

import { OrgLinks } from '../../components/OrgLinksFooter/OrgLinksFooter.constants';
import { useKustomerTokenQuery } from './graphql/kustomer.generated';
import { useKustomer } from './kustomer';

// ────────────────────────────────────────────────────────────────────────────────
LOG.enable('KUSTOMER');
const logger = LOG.extend('KUSTOMER');

// ───── Feature Flagged Contact Us ────────────────────
export const useContactUsHandler = (
  isKustomerEnabled: boolean,
  source: 'account' | 'org-links',
) => {
  const { track } = useTelemetry();
  const { open: openKustomer, openingKustomer } = useKustomer();

  const handleContactUs = useCallback(async () => {
    track('contact_us', {
      source,
      destination: isKustomerEnabled ? 'kustomer' : 'website',
    });

    if (isKustomerEnabled) openKustomer();
    else {
      await openBrowserAsync(OrgLinks.contact, {
        windowFeatures: { target: '_blank' },
      });
    }
  }, [source, isKustomerEnabled, openKustomer, track]);

  return {
    handleContactUs,
    handlingContactUs: openingKustomer,
  };
};

// Auto start Kustomer.
export const useKustomerAutoStart = () => {
  const { isKustomerEnabled, start } = useKustomer();
  const { isMobile } = useBreakpointHelpers();
  const isMobileWeb = isMobile && Platform.OS === 'web';

  // Using reference to avoid re-render issues.
  const startReference = useRef<() => void>();

  // eslint-disable-next-line functional/immutable-data
  startReference.current = start;

  useFocusEffect(
    useCallback(() => {
      if (isMobileWeb || !isKustomerEnabled) return;
      startReference?.current?.();
    }, [isMobileWeb, isKustomerEnabled]),
  );
};

// GraphQL query to fetch Kustomer token from user.
export const useKustomerToken = (isKustomerEnabled: boolean) => {
  const isCustomerSessionReady = useIsCustomerSessionReady();
  const [response, executeQuery] = useKustomerTokenQuery({
    pause: true,
  });
  const kustomerToken = response?.data?.kustomerToken?.token;

  const shouldFetchKustomerToken =
    isKustomerEnabled &&
    isCustomerSessionReady &&
    !kustomerToken &&
    !response?.fetching;

  const fetchKustomerToken = useCallback(() => {
    if (!shouldFetchKustomerToken) return;

    logger.info('Fetching token');
    executeQuery({ requestPolicy: 'network-only' });
  }, [shouldFetchKustomerToken, executeQuery]);

  return useMemo(
    () => ({
      kustomerToken,
      fetchKustomerToken,
    }),
    [fetchKustomerToken, kustomerToken],
  );
};
